import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Flex, Tooltip, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import { type BaseType } from "antd/es/typography/Base"

import { cn } from "../../../../../../../utils/cn.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"

const { Text } = Typography

type Translate = (key: string, options?: Record<string, unknown>) => string

interface ExercisedVsPlan {
  value: number | null
  color: "red" | "green" | null
}

export interface FinancialTableRow {
  key: string
  name: string
  planned: number
  actual: number
  exercised: number
  exercised_vs_plan: ExercisedVsPlan
  isHighlight?: boolean
  isIndented?: boolean
  isSeparator?: boolean
  hidePercent?: boolean
  tooltip?: string | number
}

const textVariants: Record<NonNullable<ExercisedVsPlan["color"]>, BaseType> = {
  red: "danger",
  green: "success"
}

/**
 * "Committed" holds every non-cancelled transaction, including money nobody
 * has paid yet, while "Settled" is the cash that really moved. Read the other
 * way round the table tells the opposite story, so each header states its own
 * meaning.
 */
const ColumnHeader = ({ label, hint }: { label: string; hint: string }) => (
  <Flex className="items-center justify-end gap-1">
    <span>{label}</span>
    <Tooltip title={hint}>
      <ExclamationCircleOutlined className="opacity-60" />
    </Tooltip>
  </Flex>
)

export const getColumns = (t: Translate): ColumnsType<FinancialTableRow> => [
  {
    title: t("financeSnapshot.columns.item"),
    dataIndex: "name",
    key: "name",
    width: 160,
    render: (v: string, record) => (
      <Flex className="justify-between">
        <Text
          strong={record.isHighlight}
          ellipsis={{ tooltip: v }}
          className={cn(
            { "text-primary": record.isHighlight }
            // { "pl-4": record.isIndented }
          )}
        >
          {v}
        </Text>
        {record.tooltip && (
          <Tooltip title={record.tooltip}>
            <ExclamationCircleOutlined />
          </Tooltip>
        )}
      </Flex>
    )
  },
  {
    title: (
      <ColumnHeader
        label={t("financeSnapshot.columns.plan")}
        hint={t("financeSnapshot.hints.plan")}
      />
    ),
    dataIndex: "planned",
    key: "planned",
    align: "right",
    render: (v: number, record) => {
      if (record.isSeparator) return null

      return (
        <Text
          ellipsis={{ tooltip: v }}
          className={cn({ "text-primary": record.isHighlight })}
        >
          {budgetFormat(v, { currency: "" })}
        </Text>
      )
    }
  },
  {
    title: (
      <ColumnHeader
        label={t("financeSnapshot.columns.committed")}
        hint={t("financeSnapshot.hints.committed")}
      />
    ),
    dataIndex: "actual",
    key: "actual",
    align: "right",
    render: (v: number, record) => {
      if (record.isSeparator) return null

      return (
        <Text
          ellipsis={{ tooltip: v }}
          className={cn({ "text-primary": record.isHighlight })}
        >
          {budgetFormat(v, { currency: "" })}
        </Text>
      )
    }
  },
  {
    title: (
      <ColumnHeader
        label={t("financeSnapshot.columns.settled")}
        hint={t("financeSnapshot.hints.settled")}
      />
    ),
    dataIndex: "exercised",
    key: "exercised",
    align: "right",
    render: (v: number, record) => {
      if (record.isSeparator) return null

      return (
        <Text
          ellipsis={{ tooltip: v }}
          className={cn({ "text-primary": record.isHighlight })}
        >
          {budgetFormat(v, { currency: "" })}
        </Text>
      )
    }
  },
  {
    title: (
      <ColumnHeader
        label={t("financeSnapshot.columns.percent")}
        hint={t("financeSnapshot.hints.percent")}
      />
    ),
    dataIndex: "exercised_vs_plan",
    key: "exercised_vs_plan",
    align: "center",
    width: 80,
    render: (v: FinancialTableRow["exercised_vs_plan"], record) => {
      if (record.isSeparator || record.hidePercent) return null
      if (v.value === null) return <Text>-</Text>

      return (
        <Text
          type={v?.color ? textVariants?.[v?.color] : undefined}
          ellipsis={{ tooltip: `${v.value} %` }}
        >
          {v.value}%
        </Text>
      )
    }
  }
]
